import type { MutableRefObject, ReactNode, RefObject } from 'react';

import { InfoBox } from '#/components/InfoBox/InfoBox';
import { Portal } from '#/components/Portal/Portal';

import { FieldContainer } from './FieldContainer';
import { FieldDescription } from './FieldDescription';
import { FieldError } from './FieldError';
import { FieldLabel } from './FieldLabel';

type Props = {
  label?: ReactNode;
  children: ReactNode;
  description?: ReactNode;
  inputId?: string;
  labelRef?: MutableRefObject<HTMLLabelElement | null>;
  errorRef?: RefObject<HTMLDivElement | null>;
  className?: string;
  inlineLabel?: boolean;
  compactLabel?: boolean;
  hidden?: boolean;
  noMargin?: boolean;
  noWrap?: boolean;
  error?: ReactNode;
  hideErrorLabel?: boolean;
  hasFocus?: boolean;
  hasValue?: boolean;
};

export function FieldGroup({
  label,
  children,
  description,
  inputId,
  labelRef,
  errorRef,
  className,
  inlineLabel,
  compactLabel,
  hidden,
  noMargin,
  noWrap,
  error,
  hasFocus,
  hasValue,
  hideErrorLabel = false,
}: Props) {
  if (hidden) {
    return (
      <div style={{ position: 'relative', width: '100%' }}>
        {children}

        {error && (
          <InfoBox className="error" $type="danger" $marginBottom={4}>
            {error}
          </InfoBox>
        )}
      </div>
    );
  }

  const hasMarginBottom = Boolean(!inlineLabel && !noMargin);

  return (
    <FieldContainer
      className={[className, error ? 'error' : null].filter(Boolean).join(' ')} // used to scroll to error
      style={{
        position: 'relative',
        width: '100%',
        marginBottom: hasMarginBottom ? undefined : 0,
      }}
    >
      {label && (
        <FieldLabel
          htmlFor={inputId}
          ref={labelRef}
          id={inputId ? `${inputId}-label` : undefined}
          $hasFocus={hasFocus}
          $hasValue={hasValue}
          $noWrap={noWrap}
          $inline={inlineLabel}
          $compact={compactLabel}
        >
          {label}
        </FieldLabel>
      )}

      {description && (
        <FieldDescription $noWrap={noWrap}>
          <div>{description}</div>
        </FieldDescription>
      )}

      <div>{children}</div>

      {error && errorRef?.current && !hideErrorLabel && (
        <Portal container={errorRef.current}>
          <FieldError
            inlineLabel={inlineLabel}
            errorMessage={error}
            visuallyHiddenLabel={label}
            inputId={inputId}
            noWrap={noWrap}
          />
        </Portal>
      )}
      {error && !errorRef?.current && !hideErrorLabel && (
        <FieldError
          inlineLabel={inlineLabel}
          errorMessage={error}
          visuallyHiddenLabel={label}
          inputId={inputId}
          noWrap={noWrap}
        />
      )}
    </FieldContainer>
  );
}
